// User Statistics Dashboard - Enhanced Version
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, TrendingUp, Calendar, Flame, Heart,
  Dumbbell, BookOpen, Target, Droplets, Moon,
  BarChart3, Activity, Award, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Skeleton component
function Skeleton({ className }) {
  return <div className={`animate-pulse rounded-md bg-muted ${className}`} />;
}

function StatsSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="p-4 rounded-xl bg-card border">
          <Skeleton className="h-4 w-20 mb-2" />
          <Skeleton className="h-8 w-16" />
        </div>
      ))}
    </div>
  );
}

export default function StatsPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week');

  useEffect(() => {
    fetchStats();
  }, [timeRange]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      const [moodRes, workoutRes, journalRes, habitsRes, waterRes] = await Promise.all([
        fetch(`${API_URL}/api/mood-logs`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : []).catch(() => []),
        
        fetch(`${API_URL}/api/workouts/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : {}).catch(() => ({})),
        
        fetch(`${API_URL}/api/journal`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : []).catch(() => []),
        
        fetch(`${API_URL}/api/habits`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : []).catch(() => []),
        
        fetch(`${API_URL}/api/water-logs`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : []).catch(() => [])
      ]);

      const now = new Date();
      const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
      
      const filterByRange = (items, dateField = 'created_at') => {
        const startDate = timeRange === 'week' ? weekAgo : timeRange === 'month' ? monthAgo : new Date(0);
        return items.filter(item => new Date(item[dateField]) >= startDate);
      };

      const moodLogs = Array.isArray(moodRes) ? filterByRange(moodRes) : [];
      const journals = Array.isArray(journalRes) ? filterByRange(journalRes) : [];
      const habits = Array.isArray(habitsRes) ? habitsRes : [];
      const waterLogs = Array.isArray(waterRes) ? filterByRange(waterRes, 'date') : [];

      const avgMood = moodLogs.length > 0 
        ? (moodLogs.reduce((sum, m) => sum + (m.level || 0), 0) / moodLogs.length).toFixed(1)
        : null;

      const totalWorkouts = workoutRes.total_workouts || 0;
      const workoutStreak = workoutRes.current_streak || 0;
      
      const completedHabits = habits.filter(h => h.completed_today).length;
      const totalHabits = habits.length;
      
      const totalWater = waterLogs.reduce((sum, w) => sum + (w.glasses || 0), 0);
      const avgWater = waterLogs.length > 0 ? (totalWater / waterLogs.length).toFixed(1) : 0;

      setStats({
        mood: { average: avgMood, total: moodLogs.length },
        workouts: { total: totalWorkouts, streak: workoutStreak, thisWeek: workoutRes.workouts_this_week || 0 },
        journal: { total: journals.length, thisWeek: journals.filter(j => new Date(j.created_at) >= weekAgo).length },
        habits: { completed: completedHabits, total: totalHabits, percentage: totalHabits > 0 ? Math.round((completedHabits / totalHabits) * 100) : 0 },
        water: { average: avgWater, total: totalWater }
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-lg font-semibold">Din statistik</h1>
            <p className="text-xs text-muted-foreground">Följ din utveckling</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Time Range */}
        <div className="flex gap-2 p-1 bg-muted rounded-lg">
          {[
            { value: 'week', label: 'Vecka' },
            { value: 'month', label: 'Månad' },
            { value: 'all', label: 'Allt' }
          ].map(range => (
            <button
              key={range.value}
              onClick={() => setTimeRange(range.value)}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                timeRange === range.value 
                  ? 'bg-background shadow text-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>

        {loading ? (
          <StatsSkeleton />
        ) : stats ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              {/* Mood */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-950/30 dark:to-rose-950/30 border border-pink-100 dark:border-pink-900/50">
                <div className="flex items-center justify-between mb-2">
                  <Heart className="w-5 h-5 text-pink-500" />
                </div>
                <p className="text-2xl font-bold text-pink-600 dark:text-pink-400">
                  {stats.mood.average || '-'}
                </p>
                <p className="text-xs text-pink-600/70">Snitthumör ({stats.mood.total} loggningar)</p>
              </div>

              {/* Workouts */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 border border-emerald-100 dark:border-emerald-900/50">
                <div className="flex items-center justify-between mb-2">
                  <Dumbbell className="w-5 h-5 text-emerald-500" />
                  <div className="flex items-center gap-1">
                    <Flame className="w-4 h-4 text-orange-500" />
                    <span className="text-xs font-medium text-orange-500">{stats.workouts.streak}</span>
                  </div>
                </div>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.workouts.total}</p>
                <p className="text-xs text-emerald-600/70">Träningspass totalt</p>
              </div>

              {/* Journal */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-100 dark:border-blue-900/50">
                <div className="flex items-center justify-between mb-2">
                  <BookOpen className="w-5 h-5 text-blue-500" />
                </div>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.journal.total}</p>
                <p className="text-xs text-blue-600/70">Dagboksinlägg</p>
              </div>

              {/* Habits */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/30 dark:to-violet-950/30 border border-purple-100 dark:border-purple-900/50">
                <div className="flex items-center justify-between mb-2">
                  <Target className="w-5 h-5 text-purple-500" />
                </div>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.habits.percentage}%</p>
                <p className="text-xs text-purple-600/70">Vanor idag ({stats.habits.completed}/{stats.habits.total})</p>
              </div>
            </div>

            {/* Water */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-cyan-50 to-sky-50 dark:from-cyan-950/30 dark:to-sky-950/30 border border-cyan-100 dark:border-cyan-900/50">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-cyan-100 dark:bg-cyan-900/50 flex items-center justify-center">
                  <Droplets className="w-6 h-6 text-cyan-500" />
                </div>
                <div>
                  <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400">{stats.water.average} glas/dag i snitt</p>
                  <p className="text-sm text-cyan-600/70">Totalt {stats.water.total} glas vatten</p>
                </div>
              </div>
            </div>

            {/* Achievements */}
            <div className="p-4 rounded-xl border bg-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Prestationer
                </h3>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {[
                  { icon: '📝', label: 'Första inlägget', earned: stats.journal.total > 0 },
                  { icon: '💪', label: 'Första träningen', earned: stats.workouts.total > 0 },
                  { icon: '🔥', label: '7-dagars streak', earned: stats.workouts.streak >= 7 },
                  { icon: '📚', label: '10 inlägg', earned: stats.journal.total >= 10 },
                  { icon: '🏆', label: '30 träningar', earned: stats.workouts.total >= 30 }
                ].map((achievement, i) => (
                  <div 
                    key={i}
                    className={`flex-shrink-0 w-16 h-16 rounded-xl flex flex-col items-center justify-center ${
                      achievement.earned 
                        ? 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800' 
                        : 'bg-muted opacity-50'
                    } border`}
                    title={achievement.label}
                  >
                    <span className="text-2xl">{achievement.icon}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            Kunde inte ladda statistik
          </div>
        )}
      </main>
    </div>
  );
}
